import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useState } from "react";
import { ArrowRight, Check, Plus, Minus } from "lucide-react";
import { useI18n, pickLang } from "@/lib/I18nProvider";
import { PRICING as PRICING_I18N } from "@/lib/i18n";
import { FAQS, PAGE } from "@/lib/pageContent";

function FAQItem({ q, a, i }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10" data-testid={`faq-item-${i}`}>
      <button
        data-testid={`faq-toggle-${i}`}
        onClick={() => setOpen((s) => !s)}
        className="w-full py-5 md:py-6 flex items-center justify-between text-left gap-4 md:gap-6 group"
      >
        <span className="font-display text-base md:text-xl font-semibold text-white group-hover:text-[#1FA8FF] transition-colors">{q}</span>
        <span className="w-8 h-8 md:w-9 md:h-9 rounded-full border border-white/15 flex items-center justify-center shrink-0 group-hover:border-[#1FA8FF] transition-colors">
          {open ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      {open && (
        <div className="pb-6 text-slate-200 leading-relaxed max-w-3xl">{a}</div>
      )}
    </div>
  );
}

export default function Pricing() {
  const { lang, t } = useI18n();
  const PRICING = pickLang(PRICING_I18N, lang);
  const faqs = pickLang(FAQS, lang);
  const p = pickLang(PAGE, lang);

  return (
    <div data-testid="pricing-page">
      <section className="relative overflow-hidden bg-radial-glow">
        <div className="absolute inset-0 bg-grid opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-32">
          <div className="overline">{t("nav.pricing")}</div>
          <h1 className="font-display mt-6 text-4xl sm:text-5xl md:text-7xl font-bold tracking-tighter leading-[1.02] md:leading-[0.95] max-w-4xl text-white">
            {p.pricing_title_a} <span className="gradient-text">{p.pricing_title_b}</span>
          </h1>
          <p className="mt-8 text-base md:text-xl text-slate-200 max-w-2xl leading-relaxed">{p.pricing_subtitle}</p>
        </div>
      </section>

      <section className="pb-16 md:pb-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {PRICING.map((tier, i) => (
              <motion.div
                key={tier.name}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: i * 0.07 }}
                className={`relative rounded-3xl p-7 md:p-10 flex flex-col ${
                  tier.highlighted ? "gradient-border" : "glass"
                }`}
                style={tier.highlighted ? { boxShadow: "0 30px 80px -20px rgba(31, 168, 255, 0.35)" } : {}}
                data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full gradient-bg text-black font-mono text-[10px] uppercase tracking-widest font-bold whitespace-nowrap">
                    {t("common.most_popular")}
                  </div>
                )}
                <h3 className="font-display text-2xl font-bold tracking-tight text-white">{tier.name}</h3>
                <p className="mt-2 text-sm text-slate-300">{tier.tagline}</p>

                <div className="mt-7 md:mt-8 flex items-end gap-2">
                  <div className="font-display text-4xl sm:text-5xl md:text-6xl font-bold tracking-tighter text-white">{tier.price}</div>
                  {tier.period && <div className="text-slate-300 mb-2">{tier.period}</div>}
                </div>

                <ul className="mt-7 md:mt-8 space-y-3 flex-1">
                  {tier.features.map((f, j) => (
                    <li key={j} className="flex gap-3 items-start text-sm text-slate-100">
                      <span className="mt-0.5 w-5 h-5 rounded-full gradient-bg flex items-center justify-center shrink-0">
                        <Check size={12} strokeWidth={3} className="text-black" />
                      </span>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
                  className={`mt-8 md:mt-10 inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 font-semibold transition-all ${
                    tier.highlighted ? "gradient-bg text-black hover:shadow-[0_12px_40px_rgba(31,168,255,0.35)]" : "border border-white/15 text-white hover:border-[#1FA8FF] hover:bg-white/[0.06]"
                  }`}
                >
                  {tier.cta} <ArrowRight size={16} />
                </Link>
              </motion.div>
            ))}
          </div>

          <p className="mt-10 md:mt-12 text-center text-sm text-slate-300 max-w-3xl mx-auto">{p.pricing_disclaimer}</p>
        </div>
      </section>

      <section className="py-16 md:py-24 border-t border-white/10 bg-[#324567]">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <div className="text-center mb-10 md:mb-12">
            <div className="overline justify-center inline-flex">{p.pricing_faq_overline}</div>
            <h2 className="font-display mt-4 text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white">
              {p.pricing_faq_title_a} <span className="gradient-text">{p.pricing_faq_title_b}</span>
            </h2>
          </div>
          <div>
            {faqs.map((f, i) => (
              <FAQItem key={i} i={i} q={f.q} a={f.a} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-24">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-display text-3xl sm:text-4xl md:text-5xl font-bold tracking-tighter text-white">
            {p.pricing_cta_title_a} <span className="gradient-text">{p.pricing_cta_title_b}</span>
          </h2>
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <Link to="/demo" data-testid="pricing-cta-demo" className="btn-primary">{t("cta.book_demo")} <ArrowRight size={18} /></Link>
            <Link to="/contact" data-testid="pricing-cta-contact" className="btn-ghost">{t("cta.talk_sales")}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
